import {
  AnalyticsEventName,
  DUPE_COMPARE_TO_COOKIE_KEY,
} from '@dupe/common/constants'
import { getShopKey, imgProxy, isProxiedUrl } from '@dupe/common/helpers'
import type { SerpLensResponse } from '@dupe/common/types'
import { ArrowDownIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { getCookie } from 'cookies-next'
import { getBroadcastChannel } from '~/src/lib/broadcastChannel'
import { inputTypeFromResultSource } from '~/src/lib/entryModality'
import { decodeHTML } from 'entities'
import { motion } from 'framer-motion'
import { GlobeIcon } from 'lucide-react'
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { NextSeoProps } from 'next-seo'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { getDomain } from 'tldts'
import AmazonPriceNotice from '~/src/components/AmazonPriceNotice'
import Button from '~/src/components/Button'
import CanvasImage from '~/src/components/CanvasImage'
import DupeResults from '~/src/components/DupeResults'
import { ResultSortProvider } from '~/src/context/ResultSortContext'
import ExtensionFloatingButton from '~/src/components/ExtensionFloatingButton'
import ExtensionInstallBanner from '~/src/components/ExtensionInstallBanner'
import Layout from '~/src/components/Layout'
import LottiePlayer from '~/src/components/LottiePlayer'
import OurPicks from '~/src/components/OurPicks'
import PageLoader from '~/src/components/PageLoader'
import ResultImagesCarouselModal from '~/src/components/ResultImagesCarouselModal'
import ResultsErrorForm from '~/src/components/ResultsErrorForm'
import VerifiedBadge from '~/src/components/VerifiedBadge'
import EditProductModal from '~/src/components/admin/EditProductModal'
import ExtensionModalContent from '~/src/components/modals/ExtensionModalContent'
import { useAppContext } from '~/src/context/AppProvider'
import { useAuthContext } from '~/src/context/AuthProvider'
import { CanvasProvider, useCanvasContext } from '~/src/context/CanvasProvider'
import { OfferAdProvider } from '~/src/context/OfferAdProvider'
import { ResultsResp, queryResultsResp } from '~/src/db/queryResultsResp'
import { useAmpImpression } from '~/src/hooks/useAmpImpression'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthUser from '~/src/hooks/useAuthUser'
import useCaptureUTMParams from '~/src/hooks/useCaptureUTMParams'
import useForceAnonymousAuth from '~/src/hooks/useForceAnonymousAuth'
import useGetDupesTwoPhase from '~/src/hooks/useGetDupesTwoPhase'
import useGetParsedResults from '~/src/hooks/useGetParsedResults'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import usePageBottom from '~/src/hooks/usePageBottom'
import useSelectedCountry from '~/src/hooks/useSelectedCountry'
import useShowIntercom from '~/src/hooks/useShowIntercom'
import { FeatureFlags } from '@dupe/common/constants'
import {
  convertToRedirectUrl,
  createPublicUrl,
  currencyFormatter,
  extractPriceFromResult,
  fixUrl,
  isDupeUrl,
} from '~/src/lib/helpers'
import { DUPES_RESULT_PAGE_MATCH_LIMIT } from '~/src/lib/dupesResultLimits'
import { isPossibleUrlHash } from '~/src/lib/isPossibleUrlHash'
import lookupUrlFromHash from '~/src/lib/lookupUrlFromHash'
import { filterProductsByTitle } from '~/src/lib/productTitleHelper'
import slugFromResultTitle from '~/src/lib/slugFromResultTitle'
import { cn } from '~/src/lib/utils'
import {
  PARTNER_PIN_SORT_CRITERIA,
  parsePartnerPin,
  partnerPinSortVariant,
} from '~/src/components/chat/organicOrder'
import useTrackPerformedDupe from '~/src/hooks/useTrackPerformedDupe'
import useBroadcastRedirectUrl from '~/src/hooks/useBroadcastRedirectUrl'
import { useAffiliateRouterBroadcastWiring } from '~/src/hooks/useAffiliateRouterArm'
import {
  routerCategorySignalFromResult,
  toTrackedCategoryGroup,
} from '~/src/lib/affiliateRouter'

declare global {
  interface Window {
    fabric: any
  }
}

const arrayOfStrings = (arr: any) =>
  arr.every((item: any) => typeof item === 'string')

interface PageProps {
  hash: string
  resultData: ResultsResp
}

const UrlCatchAllPageComponent: NextPage<PageProps> = ({
  resultData: resultDataFromDb,
}) => {
  const analytics = useAnalytics()
  const router = useRouter()
  const searchParam = router.query.s as string
  const refParam = router.query.ref as string
  const homeDiscoverParam = refParam === 'home-discover'
  const urlParam = router.query.url as string
  const dupeParam = router.query.dupe as string

  const { hash } = useAppContext()
  const { openAuthModal } = useAuthContext()
  const { authUser, isAdmin } = useAuthUser()
  const { tempUserId } = useGetTempUserId()
  const { value: selectedCountry } = useSelectedCountry()
  const utmParams = useCaptureUTMParams()

  const {
    loadingDupes: loadingDupesData,
    selectedRectHash,
    setMainImage,
    mainImage: contextMainImage,
    imageOverride,
  } = useCanvasContext()

  const [shouldShowLoader, setShouldShowLoader] = useState(
    !resultDataFromDb?.url || router.isFallback || !router.isReady
  )

  const [mainImageLoaded, setMainImageLoaded] = useState(false)
  const [showError, setShowError] = useState(false)
  const [focusedImageChanged, setFocusedImageChanged] = useState(false)

  const bottomReached = usePageBottom()

  const resultDataFromDbCreatedAt = resultDataFromDb?.createdAt
  const staleDate = Date.now() - 24 * 60 * 60 * 1000 // 24 hours
  const resultDataFromDbStale = resultDataFromDbCreatedAt
    ? new Date(resultDataFromDbCreatedAt).getTime() < staleDate
    : true

  const skip =
    (resultDataFromDb?.image && !resultDataFromDbStale) || router.isFallback

  const { enableIntercom } = useShowIntercom({
    customAttributes: {
      hash: hash,
      selectedCountry,
      userId: authUser?.uid,
      tempUserId,
    },
  })

  const {
    data: resultsRespData,
    isLoading: loadingResults,
    error: resultsError,
    updateFocusedImage,
  } = useGetParsedResults({
    hash,
    skip,
  })

  const resultData = resultsRespData || resultDataFromDb
  const resultTempUserId = resultData?.tempUserId
  const resultUserId = resultData?.userId

  const seoMetadata = resultData?.seoMetadata
  const resultSlug = slugFromResultTitle(
    seoMetadata?.titleOverride || resultData?.title
  )

  const shop = resultData?.shop
  const shopKey = resultData?.shopKey || getShopKey(urlParam || resultData?.url)

  const isAmazon = shopKey.indexOf('amazon.') > -1
  const isEtsy = shopKey.indexOf('etsy.') > -1
  const isEbay = shopKey?.indexOf('ebay.') > -1
  const isSerpAPI = shopKey?.indexOf('serpapi.com') > -1
  const isGStatic = shopKey?.indexOf('gstatic.com') > -1

  const isSourceImageUrl = resultData?.source === 'image-url'

  const hideShopDetails = shop?.metadata?.hide_details === true

  // Similar to above but we will show the image but not the item link or shop link
  const hideShopLinkDetails =
    isAmazon || isSourceImageUrl || isSerpAPI || isGStatic

  const potentialScam = !!shop?.potentialScam

  // Check user id vs user id on results - is no results yet then default to true
  const isOwnResults =
    tempUserId === resultTempUserId ||
    (!!authUser && authUser?.uid === resultUserId)

  const productTitle = decodeHTML(resultData?.title || '')
  const routerCategorySignal = routerCategorySignalFromResult({
    resultData,
    resultDataFromDb,
    shopCategories: shop?.categories,
  })
  const productCategory = routerCategorySignal.category ?? undefined
  const mainImage = fixUrl(resultData?.image || resultData?.dataDetected?.image)

  const extraImages = (resultData?.dataDetected?.images || []).filter(
    (img) => !!img
  )

  const [focusedImage, setFocusedImage] = useState<string>(mainImage)

  // Check if two-phase loading is enabled (quick internal + full external results)
  const { featureOn: twoPhaseLoadingEnabled } = useIsFeatureOn(
    FeatureFlags.TwoPhaseLoading
  )

  // `sponsored-picks-rail` (v1) is RETIRED 2026-09-18. PostHog had it collapsed
  // to 100% `control` / 0% `test`, so the sponsored-carousel arm served nobody
  // and the organic feed always used the default (Recommended) sort. Both the
  // flag read and the rail arm are gone; the surviving control behaviour is
  // inlined below — the vertical `OurPicks` list, no organic sort override, and
  // `sortReady` permanently true (nothing async to wait on any more).
  // See docs/features/active/sponsored-picks-rail/.

  const {
    refresh: loadDupes,
    matches: allMatches,
    isLoading,
    isLoadingMore: _isLoadingMore,
    refreshing,
    error: dupesError,
    fullData: dupesFullData,
    inputType: dupesInputType,
  } = useGetDupesTwoPhase(focusedImage, {
    hash,
    rectHash: selectedRectHash,
    q: productTitle,
    productCategory,
    selectedCountry,
    limit: DUPES_RESULT_PAGE_MATCH_LIMIT,
    skipQuick: !twoPhaseLoadingEnabled, // Only use quick endpoint if feature is enabled
    trackPartnerPinExposure: true,
  })

  // Partner-pin test arm, off the FULL response only — the quick endpoint
  // carries no arm, and `allMatches` switches to `fullData.matches` when it
  // lands. Absent ⇒ today's page, unchanged.
  const partnerPin = parsePartnerPin(dupesFullData?.partnerPin)
  const partnerPinDsv = partnerPinSortVariant(partnerPin)
  // pin2: the picks list holds only the server's `pinned` rows; every other
  // row — other picks included — stays in the grid in served order. pin2 views
  // stamp 'partnerPin' (not unset) so they stay out of `ctr_by_cell`'s
  // ranking scores, which pool 'recommended' OR unset.
  const isPin2 = partnerPin?.arm === 'pin2'
  const isFrontRow = (match: SerpLensResponse['visual_matches'][number]) =>
    isPin2 ? match.pinned === true : !!match.pick
  const partnerPinSortCriteria = isPin2 ? PARTNER_PIN_SORT_CRITERIA : null

  // Show loading state for initial load, but allow results to display while loading more
  const loadingDupes =
    (refreshing || isLoading || loadingDupesData) && !allMatches.length

  const _visualMatches: SerpLensResponse['visual_matches'] = [...allMatches]
    .filter((match) => !isFrontRow(match))
    .filter((match) => match.identifier !== 'dupe-factory')
  const visualMatches =
    !imageOverride && focusedImage === mainImage
      ? filterProductsByTitle(productTitle, _visualMatches)
      : _visualMatches

  const _ourPicks: SerpLensResponse['visual_matches'] = allMatches.filter(
    (match) => isFrontRow(match)
  )
  const ourPicks =
    !imageOverride && focusedImage === mainImage
      ? filterProductsByTitle(productTitle, _ourPicks)
      : _ourPicks

  const hasAmazonMatches = allMatches.some((match) =>
    (match.source || '').toLowerCase().includes('amazon')
  )

  const showPriceNotice = isAmazon || hasAmazonMatches

  const error = resultsError || dupesError

  const { price, currency } = extractPriceFromResult(resultData)
  const formattedPrice = price
    ? currencyFormatter(price, {
        currency: currency || 'USD',
        round: true,
      })
    : resultData?.dataDetected?.offerPrice

  const ogImageUrl = createPublicUrl(`/results-og/${hash || resultData?.hash}`)
  // AMP source monetization: the `/api/parse` response decorates the payload
  // with `hiddenPrice` (always true on Amazon source) and `monetization`
  // (only on AMP hit). The DB-loaded `ResultsResp` type doesn't model these
  // — they're attached at the controller layer post-DB. Narrow via a typed
  // view here rather than widening the shared type, since these fields are
  // only meaningful on the live parse response.
  const sourceMonetization = (
    resultData as
      | (typeof resultData & {
          monetization?: {
            type: 'amp'
            clickUrl: string
            impressionUrl?: string
          }
          hiddenPrice?: boolean
        })
      | null
  )?.monetization
  const sourceHiddenPrice = !!(resultData as { hiddenPrice?: boolean } | null)
    ?.hiddenPrice
  const isAmpHit = sourceMonetization?.type === 'amp'
  const sourceClickUrl = isAmpHit
    ? sourceMonetization.clickUrl
    : resultData?.url
  const redirectUrl = convertToRedirectUrl(
    sourceClickUrl,
    {
      source: 'result-product',
      key: shopKey,
      hash: hash || resultData?.hash,
      partner: !!shop?.verified,
      // Entry modality -> link_clicks.metadata.input_type (DUP-2138). The
      // source-product clickout is a real revenue surface and carried no
      // record of how the search started.
      //
      // The parse result loads BEFORE the dupes response `dupesInputType` comes
      // from, so this anchor used to render with no modality and a fast click
      // wrote a `src=result-product` row without one. The stored-result-source
      // fallback closes that window: it is available on the same render as the
      // anchor. Unlike the broadcast redirect, this href is rebuilt freely on
      // re-render and writes no row until it is clicked, so preferring the
      // authoritative value with a synchronous fallback is safe here.
      inputType:
        dupesInputType ?? inputTypeFromResultSource(resultData?.source),
      monetized: isAmpHit ? 'amp' : null,
    },
    utmParams
  )

  // AMP source-product impression: fires once after the source image
  // container has been continuously visible for 15s. Server-side fire was
  // removed to avoid double-counting (see asin.ts decorateParseResponseWithAmp).
  const sourceImpressionRef = useRef<HTMLDivElement | null>(null)
  useAmpImpression(sourceImpressionRef, sourceMonetization?.impressionUrl, {
    source: 'source-product',
    advertiser: 'Amazon',
    title: productTitle || null,
    resultIndex: 0,
  })

  const nextSeo: NextSeoProps = {
    title: productTitle || 'See the products I found',
    canonical: createPublicUrl(`/chats/${hash || resultData?.hash}`),
    openGraph: {
      images: [
        {
          url: ogImageUrl,
        },
      ],
    },
    noindex: true,
    nofollow: true,
  }

  const { ref: detailsInViewRef, inView: detailsInView } = useInView({
    skip: hideShopDetails,
    triggerOnce: true,
  })

  // trigger analytics event for "Viewed Dupe Result Page"
  useEffect(() => {
    if (!hash) return
    if (!shopKey) return

    analytics.track(AnalyticsEventName.ViewedDupeResultPage, {
      hash,
      shopKey,
    })

    // Facebook Pixel - track "View Content"
    if (window.fbq) {
      window.fbq('track', 'ViewContent')
    }

    // The `analytics` object causes this useEffect to
    // run multiple times, so we exclude it from the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, shopKey])

  // Show error logic
  useEffect(() => {
    if (!router?.isReady) return
    if (shouldShowLoader) return
    if (loadingResults) return

    if (!!resultsError || (resultData && !mainImage)) {
      setShowError(true)
    }
  }, [
    loadingResults,
    mainImage,
    resultData,
    resultsError,
    router?.isReady,
    shouldShowLoader,
  ])

  // Show loader logic
  useEffect(() => {
    if (!!error) {
      setShouldShowLoader(false)
      return
    }

    if (resultData && focusedImage) {
      setShouldShowLoader(false)
      return
    }

    if (
      !router?.isReady ||
      router?.isFallback ||
      (!resultData && loadingResults) ||
      !mainImageLoaded
    ) {
      setShouldShowLoader(true)
    }
  }, [
    error,
    focusedImage,
    loadingResults,
    resultData,
    router?.isFallback,
    router?.isReady,
    mainImageLoaded,
  ])

  // Set main image on mount for Canvas provider.
  // This is needed for new results since the db does not have the image yet
  useEffect(() => {
    if (!focusedImageChanged && !!contextMainImage) return

    const img = focusedImage || mainImage

    setMainImage(img)

    if (!focusedImage) {
      setFocusedImage(img)
    }
  }, [
    mainImage,
    focusedImage,
    setMainImage,
    focusedImageChanged,
    contextMainImage,
  ])

  // Reload dupes when focused image changes
  useEffect(() => {
    if (!focusedImageChanged) return
    loadDupes({ refresh: true })
  }, [loadDupes, focusedImageChanged])

  // Track "Performed Dupe" event and update the URL.
  //
  // `outcome` is what the search actually produced. It is passed as live state
  // (read through a ref inside the hook at fire time) rather than captured on
  // mount, because on mount the dupes request has not returned yet — the whole
  // point of `results_count` is that it describes a finished search.
  useTrackPerformedDupe({
    hash,
    dupeParam,
    urlParam,
    categoryGroup: toTrackedCategoryGroup(
      routerCategorySignal.category ?? null
    ),
    // `DupeInputType` is `'image' | 'url' | null` — a search the API classified
    // as `url` must not be labelled `text` just because `router.query.url` is
    // absent on this render.
    searchType:
      dupesInputType === 'image'
        ? 'image'
        : dupesInputType === 'url'
          ? 'url_prefix'
          : 'text',
    // This surface opts IN, but it is a very low-volume slice of the
    // DUP-2080/DUP-2091 conversions (~128 `Performed Dupe`/wk vs chat's
    // ~126k): `?dupe=true` is only ever set by `middleware.ts` for `/chats/*`
    // and the sidepanel, never for `/[hash]`, so it lands here only when a
    // caller sets it explicitly. The extension sidepanel results page shares
    // this hook and stays out by omission.
    trackAdsConversions: true,
    outcome: {
      // Positive completion only. `!isLoading` would ALSO be true in the tick
      // before the request starts, which reports every page load as a finished
      // zero-result search — caught in the browser, not in review. A failed
      // request does count as settled: we know it produced nothing for this
      // user, and holding the event back would only lose it.
      ready: !!dupesFullData || !!allMatches.length || !!dupesError,
      phase: dupesFullData
        ? 'full'
        : allMatches.length
          ? 'quick'
          : dupesError
            ? 'error'
            : null,
      resultsCount: visualMatches.length,
      picksCount: ourPicks.length,
      priceAnchor: typeof price === 'number' ? price : null,
      priceAnchorCurrency: currency || null,
      query: dupesFullData?.q ?? productTitle ?? null,
    },
  })

  // Pre-build redirect URL for broadcast
  const baseShouldBroadcast = !!(
    router?.isReady &&
    !!productTitle &&
    !searchParam &&
    !homeDiscoverParam
  )
  const affiliateRouterBroadcast = useAffiliateRouterBroadcastWiring({
    eligible: baseShouldBroadcast,
    surface: 'catch-all-results-broadcast',
  })
  const shouldBroadcast = affiliateRouterBroadcast.shouldBroadcast
  const broadcastRouterArm = affiliateRouterBroadcast.routerArm
  const broadcastRouterEnabled = affiliateRouterBroadcast.routerEnabled
  const {
    redirectUrl: broadcastRedirectUrl,
    routerDecision: broadcastRouterDecision,
  } = useBroadcastRedirectUrl({
    productTitle,
    routerCategory: routerCategorySignal,
    routerArm: broadcastRouterArm,
    routerArmReady: affiliateRouterBroadcast.isReady,
    routerEnabled: broadcastRouterEnabled,
    // Entry modality -> link_clicks.metadata.input_type (DUP-2139). Prefer the
    // authoritative server value; fall back to the stored result source, which
    // is available synchronously. The broadcast redirect is built ONCE and a
    // late value never lands, so without the fallback most rows would be null.
    inputType: dupesInputType ?? inputTypeFromResultSource(resultData?.source),
    // One results hash = one logical search. Re-arms the modality freeze on a
    // soft navigation to another hash, which reuses this component (`_app.tsx`
    // renders `<Component>` with no `key`) and would otherwise stamp this
    // search's modality onto the next one's row.
    modalityKey: resultData?.hash ?? hash ?? null,
    enabled: shouldBroadcast,
  })

  // Broadcast for "compare to" feature on homepage
  useEffect(() => {
    if (!shouldBroadcast) return
    if (!broadcastRedirectUrl) return // Wait for redirect URL to be ready

    const dupeCompareTo = getCookie(DUPE_COMPARE_TO_COOKIE_KEY)
    const channel = getBroadcastChannel('dupe.com')

    // send window message to open dupe.com in new tab with pre-built redirect URL
    channel.postMessage({
      dupeCompareTo,
      productTitle,
      redirectUrl: broadcastRedirectUrl,
      routerDecision: broadcastRouterDecision,
      ...(broadcastRouterArm ? { routerArm: broadcastRouterArm } : {}),
    })
  }, [
    shouldBroadcast,
    productTitle,
    broadcastRedirectUrl,
    broadcastRouterDecision,
    broadcastRouterArm,
  ])

  // Open Intercom on error
  useEffect(() => {
    if (!showError) return

    enableIntercom()
  }, [enableIntercom, showError])

  // Force anonymous auth for users who have not authenticated
  // after running (n) dupes
  useForceAnonymousAuth({ resultData, isOwnResults })

  // If Etsy and we don't have existing results,
  // show Extension install content
  if (isEtsy && !resultDataFromDb?.shopKey) {
    return (
      <Layout nextSeo={nextSeo} fullWidth className='p-4 md:p-8'>
        <div className='mx-auto flex max-w-screen-md flex-col items-center md:py-12'>
          <div className='flex max-w-[250px] flex-col items-center space-x-1 text-center md:max-w-none md:flex-row md:text-left'>
            <LottiePlayer
              src='https://lottie.host/14011ae0-02c7-4f1c-ba39-f10cb4ee3d4c/s9kAlTA0kL.lottie'
              loop
              autoplay
              style={{
                height: 40,
                width: 40,
              }}
            />
            <div className='md:hidden'>
              Please install our desktop plugin to find similar products from
              Etsy
            </div>
            <div className='hidden md:block'>
              You&#39;ll need our plugin to find similar products from Etsy
            </div>
          </div>
          <div className='m-4 overflow-hidden rounded-2xl border-2 border-black'>
            <ExtensionModalContent />
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <OfferAdProvider productTitle={productTitle}>
      <Layout
        showExtensionHellobar
        nextSeo={nextSeo}
        bottomReached={bottomReached}
        className='min-h-screen max-w-full overflow-hidden'
        headerClassName='border-b-2 border-b-black'
        fullWidth
        hideBorders
        id='resultsContainer'
      >
        <div className='mx-auto min-h-screen max-w-screen-sm border-black md:border-r-2 md:border-l-2'>
          {!shouldShowLoader && !showError && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 1 }}
              key='content'
            >
              {/* Add ExtensionFloatingButton */}
              <ExtensionFloatingButton dupesData={allMatches} />

              {!hideShopDetails && (
                <>
                  {/* Product Image */}
                  <div className='relative' ref={sourceImpressionRef}>
                    <div
                      className={cn('relative z-10 bg-gray-50', {
                        'border-b-2 border-b-black': !hideShopLinkDetails,
                      })}
                    >
                      <CanvasImage
                        details={{
                          shopKey,
                          hash,
                          source: isSourceImageUrl
                            ? 'image-url'
                            : 'product-url',
                        }}
                      >
                        {({ canvasWidth, canvasHeight }) => (
                          <motion.div
                            className={cn(
                              'flex items-center justify-center bg-gray-50 transition-all',
                              {
                                'min-h-[300px] md:min-h-[500px]': !canvasHeight,
                              }
                            )}
                            style={{
                              height: canvasHeight || 'auto',
                            }}
                            initial={{ opacity: 0 }}
                            animate={{
                              opacity: 1,
                            }}
                            exit={{
                              opacity: 0,
                            }}
                            transition={{
                              delay: 0.5,
                            }}
                          >
                            <Image
                              src={focusedImage}
                              alt=''
                              width={canvasWidth}
                              height={canvasHeight}
                              unoptimized
                              priority
                              onLoad={() => {
                                setMainImageLoaded(true)
                              }}
                              onError={() => {
                                setMainImageLoaded(true)

                                if (!isProxiedUrl(mainImage)) {
                                  setFocusedImage(imgProxy(mainImage))

                                  setFocusedImageChanged(true)
                                  // trigger revalidation
                                  fetch(`/api/revalidate?path=/${hash}`, {
                                    method: 'POST',
                                  })
                                }
                              }}
                              style={{
                                aspectRatio: `${canvasWidth / canvasHeight}`,
                              }}
                              className='w-full object-contain'
                              onContextMenu={(e) => e.preventDefault()}
                            />
                          </motion.div>
                        )}
                      </CanvasImage>

                      {/* Bouncing Arrow */}
                      {!detailsInView && (
                        <motion.div
                          className='fixed inset-x-0 bottom-0 z-10 flex items-center justify-center p-4'
                          initial={{ opacity: 0, y: 80 }}
                          animate={{
                            opacity: 1,
                            y: 0,
                            translateY: [0, -10, 0],
                          }}
                          transition={{
                            translateY: {
                              duration: 1.5,
                              repeat: Infinity,
                              ease: 'easeInOut',
                            },
                            delay: 1.0,
                          }}
                          exit={{ opacity: 0, y: 80 }}
                        >
                          <button
                            className='rounded-full bg-white p-2 text-black'
                            onClick={() => {
                              document
                                .getElementById('resultDetails')
                                ?.scrollIntoView({
                                  behavior: 'smooth',
                                })
                            }}
                          >
                            <ArrowDownIcon className='h-7 w-7' />
                          </button>
                        </motion.div>
                      )}

                      {/* Admin Actions (Feature and Edit) */}
                      {isAdmin && resultData && (
                        <div className='absolute right-4 bottom-4 z-40 flex items-center space-x-2'>
                          {!!seoMetadata?.qualityScore && (
                            <Button
                              variant='secondary'
                              size='sm'
                              onClick={() => {
                                window.open(
                                  `/product/${hash}/${resultSlug}`,
                                  '_blank'
                                )
                              }}
                            >
                              <GlobeIcon className='h-4 w-4 text-blue-500' />
                            </Button>
                          )}
                          <EditProductModal resultsData={resultData} />
                        </div>
                      )}
                    </div>

                    {/* Extra Images Carousel */}
                    {!!extraImages.length &&
                      Array.isArray(extraImages) &&
                      arrayOfStrings(extraImages) && (
                        <div className='px-4 py-3 md:absolute md:top-4 md:right-[-65px] md:bottom-auto md:w-[50px] md:p-0'>
                          <ResultImagesCarouselModal
                            images={extraImages}
                            onClickCallback={(
                              img: string | undefined,
                              callback
                            ) => {
                              if (!img) return
                              if (authUser) {
                                setFocusedImage(img)
                                setFocusedImageChanged(true)

                                if (isOwnResults || !resultDataFromDb) {
                                  // update the main image in the database
                                  updateFocusedImage(img)
                                }

                                if (callback) {
                                  callback()
                                }
                              } else {
                                openAuthModal({
                                  source: 'results-image',
                                })
                              }
                            }}
                          />
                        </div>
                      )}
                  </div>

                  {/* Product Title and Price Area */}
                  <div ref={detailsInViewRef} id='resultDetails'>
                    {!hideShopLinkDetails && (
                      <div className='mb-2 space-y-2 p-4 md:p-6'>
                        {/* Potential Scam Warning */}
                        {potentialScam && authUser && (
                          <div className='mb-2 inline-flex items-center rounded-md bg-red-700 px-2 py-2 text-xs text-white/90'>
                            <strong>⚠️ Warning:</strong> site has been reported
                            as potential scam
                          </div>
                        )}

                        {/* Shop, Product Title & Button */}
                        <div className='space-y-1'>
                          {/* Shop & Product Title */}
                          <Link
                            href={redirectUrl}
                            target='_blank'
                            className={clsx('block', {
                              hidden: isSourceImageUrl,
                            })}
                            onClick={() => {
                              analytics.track(
                                AnalyticsEventName.DupeProductClicked,
                                {
                                  hash,
                                  shopKey,
                                  url: resultData?.url,
                                  redirectUrl,
                                }
                              )
                            }}
                          >
                            <div className='itmes-center flex space-x-1.5 leading-none'>
                              <span className='inline-flex items-center text-xs uppercase opacity-60'>
                                {getDomain(shopKey as string)}
                              </span>
                              {(!!shop?.verified || !!shop?.preferred) && (
                                <VerifiedBadge
                                  preferred={shop?.preferred}
                                  shopKey={shopKey ?? undefined}
                                />
                              )}
                              {isEbay && (
                                <span className='inline-flex items-center text-xs opacity-60'>
                                  #sponsored
                                </span>
                              )}
                            </div>
                            <h2
                              className='line-clamp-2 max-w-lg text-lg font-medium md:text-2xl'
                              data-testid='result-product-title'
                            >
                              {productTitle}
                            </h2>
                          </Link>

                          {!!resultData && (
                            <div className='relative flex items-center justify-between gap-2'>
                              {/* View Product / Price Button */}
                              <Link
                                href={redirectUrl}
                                target='_blank'
                                className='block'
                                onClick={() => {
                                  analytics.track(
                                    AnalyticsEventName.DupeProductClicked,
                                    {
                                      hash,
                                      shopKey,
                                      url: resultData?.url,
                                      redirectUrl,
                                    }
                                  )
                                }}
                              >
                                <>
                                  <div className='rounded-md border border-black/10 px-3 py-1 text-sm font-semibold transition-colors hover:border-black/20 hover:bg-black/5 md:text-base dark:border-white/20 dark:hover:border-white/30 dark:hover:bg-white/10'>
                                    {sourceHiddenPrice ? (
                                      <span>See price</span>
                                    ) : !!formattedPrice ? (
                                      <span>
                                        {formattedPrice}
                                        {showPriceNotice && <span>*</span>}
                                      </span>
                                    ) : (
                                      <span>View Product</span>
                                    )}
                                  </div>
                                </>
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className='min-h-screen w-full'>
                {/* Our Picks — the vertical list. Wrapped in a
                    ResultSortProvider so the picks' impressions/clicks are
                    tagged (closes the pre-existing untagged-picks gap, DUP-2138).
                    The sort/arm props are pinned to the values the context
                    default already supplied (null / null / ready) rather than
                    a `SortCriteria.Match` lens, which keeps these picks out of
                    the organic grid's sort slice. This was the `control` arm of
                    `sponsored-picks-rail` until that flag was retired on
                    2026-09-18 at 100% control; it is now the only behaviour. */}
                <ResultSortProvider
                  sortCriteria={partnerPinSortCriteria}
                  defaultSortVariant={partnerPinDsv}
                  inputType={dupesInputType}
                >
                  <OurPicks
                    resultsData={resultData}
                    ourPicks={ourPicks}
                    loadingDupes={loadingDupes}
                    className='relative pb-4 md:-mx-6 md:drop-shadow-md'
                    query={dupesFullData?.q ?? productTitle ?? null}
                    query_category={dupesFullData?.category ?? null}
                  />
                </ResultSortProvider>

                {!dupesError && (
                  <div className='relative'>
                    <ResultSortProvider
                      sortCriteria={partnerPinSortCriteria}
                      defaultSortVariant={partnerPinDsv}
                      sortReady
                      inputType={dupesInputType}
                    >
                      <DupeResults
                        selectedCountry={selectedCountry}
                        loading={!visualMatches.length && loadingDupes}
                        processing={loadingDupes}
                        resultsData={resultData}
                        visualMatches={visualMatches}
                        hasOurPicks={!!ourPicks.length}
                        query={dupesFullData?.q ?? productTitle ?? null}
                        query_category={dupesFullData?.category ?? null}
                      />
                    </ResultSortProvider>
                  </div>
                )}
              </div>

              <div
                // ref={bottomInViewRef}
                className='w-full'
              >
                <ExtensionInstallBanner appStoreCampaign='ResultsBottomBanner' />

                {/* TODO: disable while trade banner is active */}
                {/* <AppBannerModal source='results-page' /> */}
              </div>

              {showPriceNotice && (
                <div
                  id='priceNotice'
                  className='mx-auto max-w-xl space-y-1 px-4 py-4 text-center text-sm opacity-40 md:px-6'
                >
                  <AmazonPriceNotice />{' '}
                  {!!resultData?.createdAt && (
                    <span>
                      Updated{' '}
                      {new Date(resultData.createdAt).toLocaleString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </span>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {shouldShowLoader && (
            <motion.div
              key='loader'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 1,
              }}
              className='pointer-events-none flex items-center justify-center'
            >
              <PageLoader className='py-32 md:py-64' />
            </motion.div>
          )}

          {showError && (
            <div>
              <ResultsErrorForm
                resultsData={resultData}
                error={error?.message}
              />
              <ExtensionInstallBanner appStoreCampaign='ResultsErrorBottomBanner' />
            </div>
          )}
        </div>

        {/* TODO: come back to this. too many popups */}
        {/* <ExtensionInstallModal /> */}
      </Layout>
    </OfferAdProvider>
  )
}

export default (function UrlCatchAllPage(props) {
  // Ensure we're using the most up-to-date image source
  // This key forces the CanvasProvider to be recreated on navigation
  const imageKey = `${props?.hash}-${props?.resultData?.image || ''}`

  return (
    <CanvasProvider key={imageKey} image={props?.resultData?.image}>
      <UrlCatchAllPageComponent {...props} />
    </CanvasProvider>
  )
} as NextPage<PageProps>)

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    // 'blocking' — NOT `true`. With `fallback: true` Next serves the static
    // fallback shell for any unseen path and answers HTTP **200**, only
    // discovering `notFound: true` on the background regeneration. That made
    // every nonexistent path on the site report 200 on first request (this is
    // the root catch-all, so that means *every* bad URL), which tells agents
    // and scanners that every path exists — the Is Agentic audit's
    // "agent-friendly 404s" failure. 'blocking' runs getStaticProps on the
    // first request instead, so `notFound` becomes a real 404 immediately.
    //
    // Cost: the first visitor to a brand-new hash waits for getStaticProps
    // rather than getting the shell instantly. That is two indexed lookups on
    // the primary (`lookupUrlFromHash` + `queryResultsResp`), and the page
    // already renders a client loader while dupes stream in, so the visible
    // flow is unchanged — the shell it replaces was empty anyway. Bogus paths
    // are cheaper than before, because the shape gate below 404s them without
    // touching the database at all.
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const hash = params?.hash as string

  // Reject shapes no hash encoder can produce before spending a database round
  // trip (and `lookupUrlFromHash`'s replication-lag retries) on them. Same
  // 404 outcome, no query. See isPossibleUrlHash for why this can't 404 a
  // real page.
  if (!isPossibleUrlHash(hash)) {
    return {
      notFound: true,
      revalidate: 300,
    }
  }

  const url = await lookupUrlFromHash(hash)

  if (!url) {
    return {
      notFound: true,
      // Missing hash: re-check every 5 min instead of every minute. Crawlers
      // hitting nonexistent hashes were a large share of ISR writes.
      revalidate: 300,
    }
  }

  const resultData = await queryResultsResp(hash)

  const dupeUrl = isDupeUrl(resultData?.url)
  if (dupeUrl instanceof URL) {
    return {
      redirect: {
        destination: dupeUrl.pathname,
        permanent: true,
      },
    }
  }

  const oneHourInSeconds = 60 * 60 // 1 hour
  // url exists but results not ready yet: retry after 60s rather than every
  // request (revalidate: 1 regenerated on essentially every hit → huge ISR cost).
  const revalidate = !resultData ? 60 : oneHourInSeconds

  return {
    props: {
      hash,
      resultData: resultData || null,
    },
    revalidate,
  }
}
